import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { JSX, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../store';
import { IsAppOpenedInIframe, IsAppOpenedByExtApp, navigateToHome } from '../services';
import { ViewIdError } from './ViewIdError';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, NavigateFunction } from 'react-router';
import { countryCodes } from '../localization';
import CancelIcon from '@mui/icons-material/Cancel';
import { AppAuthContext } from '../services/Contexts';
import { UrlHelper } from '../services/UrlHelper';
import { ESessionStore, EUrlParams, EErrorCodes, EErrorTypes, EPageNames } from '../data/Constants';
import { AuthContextProps, useAuth } from 'oidc-react';
import { SessionStore } from '../services/SessionStore';
import { resetError } from '../store/states/ErrorSlice';
import { TFunction } from 'i18next';
import type { IProduct } from '../../types';
import type { IError } from '../../types/IApiTypes';
/**
 * Renders the error message for invalid viewId or other error pages.
 * @param {IError} error - The error object
 * @param {string} view - The current view id
 * @param {TFunction} t - Translation function
 * @param {string} token - Auth token
 * @returns {JSX.Element} The error message or error component
 */

const display404 = (error: IError, view: string, t: TFunction, token: string): JSX.Element => {
  const errorId = error.errorId;
  if (error.page === 'configure') {
    return (
      <AppAuthContext.Provider value={token}>
        <ViewIdError handleContact={handleContact} view={view} />
      </AppAuthContext.Provider>
    );
  } else if (error.page === 'noViews') {
    return <span>{t('errorMessages.viewId.noViews')}</span>;
  } else if (error.page === 'create-configuration') {
    let errorMessage;
    if (errorId) {
      errorMessage = t(`errorMessages.general.${errorId}`, {
        productId: error.data?.productId,
      });
    } else {
      // Fall back to the message from the API
      errorMessage = error.message ? error.message : t('errorMessages.general.PKGNF');
    }
    return <span>{errorMessage}</span>;
  } else {
    return <span>{error?.message ?? t('errorMessages.http.404')}</span>;
  }
};

/**
 * Renders the error message for 400 errors.
 * @param {IError} error - The error object
 * @param {TFunction} t - Translation function
 * @param {string} view - The current view id
 * @param {string} token - Auth token
 * @returns {JSX.Element} The error message or error component
 */
const display400 = (error: IError, t: TFunction, view: string, token: string): JSX.Element => {
  if (error.message && typeof error.message === 'string' && error.message.includes('MRKTNF')) {
    return (
      <span>
        {t('errorMessages.general.MRKTNF', {
          country:
            countryCodes[error?.data?.country as keyof typeof countryCodes].name +
            ' (' +
            error?.data?.country +
            ')',
        })}
      </span>
    );
  } else if (error.page === 'configure') {
    return display404(error, view, t, token);
  } else {
    return <span>{t('errorMessages.http.400')}</span>;
  }
};

/**
 * Renders the error message based on error code.
 * @param {IError} error - The error object
 * @param {string} view - The current view id
 * @param {TFunction} t - Translation function
 * @param {string} token - Auth token
 * @returns {JSX.Element} The error message or error component
 */
const displayMessage = (error: IError, view: string, t: TFunction, token: string): JSX.Element => {
  switch (error.code) {
    case 401:
      return <span>{t('errorMessages.http.401')}</span>;
    case 204:
      return <span>{error?.message ?? t('errorMessages.http.204')}</span>;
    case 400:
      return display400(error, t, view, token);
    case 404:
      return display404(error, view, t, token);
    case 403:
      return <span>{t('errorMessages.http.403')}</span>;
    default:
      return <span>{t('errorMessages.http.500')}</span>;
  }
};

/**
 * Returns the header for the error dialog.
 * @param {IError} error - The error object
 * @param {any} productSettings - Product settings from state
 * @param {TFunction} t - Translation function
 * @returns {JSX.Element} The header element
 */
const getHeader = (error: IError, productSettings: any, t: TFunction): JSX.Element => {
  if (error.code === 400 && error.page === 'create-configuration') {
    return (
      <span className="errorProduct-header">
        {error?.data?.productId +
          ' - ' +
          productSettings.productCatalog[productSettings.productCatalogDetails.page]?.find(
            (prod: IProduct) => prod.productId === error?.data?.productId
          )?.productDescription}
      </span>
    );
  }
  return <span>&nbsp; {t('dialogBox.error')}</span>;
};

/**
 * Retrieves the authentication token and auth object based on the app context.
 * @returns {{ token: string, auth: any }} The token and auth object.
 */
const GetToken = () => {
  let auth;
  let token = '';

  if (IsAppOpenedInIframe() || IsAppOpenedByExtApp()) {
    token = UrlHelper.getSearchParameter(EUrlParams.Token) ?? SessionStore.get(ESessionStore.Token);
  } else {
    auth = useAuth(); //Do not move this line outside of this function, as it will cause an error for Hybris scenario
    token = auth.userData?.access_token as string;
  }
  return { token: token, auth: auth };
};

/**
 * Handles the redirect when the Contact Us button is clicked.
 * @param {NavigateFunction} navigate - Navigation function
 * @param {AppDispatch} dispatch - Redux dispatch
 */

const handleContact = (navigate: NavigateFunction, dispatch: AppDispatch): void => {
  navigate(EPageNames.ErrorPage, {
    replace: true,
    state: { type: EErrorTypes.ContactUS },
  });
  dispatch(resetError());
};

/**
 * Handles closing the error dialog on Ok or Refresh button click.
 * @param {IError} error - The error object
 * @param {any} auth - Auth instance
 * @param {AppDispatch} dispatch - Redux dispatch
 */

const closeDialog = (
  error: IError,
  auth: AuthContextProps | undefined,
  dispatch: AppDispatch
): void => {
  if (error.code === 401) {
    auth?.signIn();
  } else if (error.code === 404 && error.page === EPageNames.Configurator) {
    globalThis.location.reload();
  } else {
    dispatch(resetError());
  }
};

/**
 * Handles navigation and error reset for authentication/authorization errors.
 * @param {IError} error - The error object
 * @param {NavigateFunction} navigate - Navigation function
 * @param {AppDispatch} dispatch - Redux dispatch
 */
const handleError = (error: IError, navigate: NavigateFunction, dispatch: AppDispatch) => {
  if (
    (IsAppOpenedByExtApp() || IsAppOpenedInIframe()) &&
    error.code === EErrorCodes.Unauthenticated
  ) {
    navigate(EPageNames.ErrorPage, {
      replace: true,
      state: { type: EErrorTypes.NotAuthenticated },
    });
    dispatch(resetError());
  }
  if (error.code === EErrorCodes.Unauthorized && error.show) {
    navigate(EPageNames.ErrorPage, {
      replace: true,
      state: { type: EErrorTypes.NotAuthorized },
    });
    dispatch(resetError());
  }
};

/**
 * Main error dialog component. Displays error messages and actions based on error state.
 * @returns {JSX.Element | null} The error dialog or null if not shown
 */
export const ErrorDialog = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { token, auth } = GetToken();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  //selectors
  const error = useSelector((state: AppState) => state.error);
  const productSettings = useSelector((state: AppState) => state.productCatalog);

  // Handle navigation for authentication/authorization errors on error state change
  useEffect(() => {
    handleError(error, navigate, dispatch);
  }, [error]);

  // To hide error dialog when configuration id is invalid
  if ((error.code === 404 || error.code === 400) && error.page === 'noViews') {
    return null;
  }

  const view = searchParams.get('viewId') ?? '';
  if (error.code && error.show) {
    return (
      <Dialog className="common-dialog-style error-dialog-style" open={!!error.code} maxWidth="md">
        <DialogTitle className=" header title">
          <Grid container sx={{ alignItems: 'center' }} className="error-header">
            <CancelIcon className="errorIcon" />
            {getHeader(error, productSettings, t)}
          </Grid>
        </DialogTitle>
        <DialogContent className="dialogContent">
          <br />
          {displayMessage(error, view, t, token)}
        </DialogContent>
        <DialogActions>
          {(error.code === 404 || error.code === 400) && error.page === 'configure' ? (
            ''
          ) : (
            <>
              <Button
                className="close-dialog text-capitalize"
                onClick={() => handleContact(navigate, dispatch)}
                data-testid="contactUs-btn"
              >
                <span>{t('button.contactUS')}</span>
              </Button>

              {/* This check added as part of missing mandatory pdm properties, where code and page is required */}
              {error.code === 404 && error.page === EPageNames.Configurator && (
                <Button
                  className="close-dialog text-capitalize"
                  onClick={() => closeDialog(error, auth, dispatch)}
                  data-testid="close-btn"
                >
                  <span>{t('button.refresh')}</span>
                </Button>
              )}

              {!IsAppOpenedInIframe() && !IsAppOpenedByExtApp() && (
                <Button
                  className="close-dialog text-capitalize"
                  onClick={() => navigateToHome(navigate)}
                  data-testid="close-btn"
                >
                  <span>{t('button.ok')}</span>
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  } else {
    return <></>;
  }
};
